
import React, { useState, useEffect } from 'react';
import { Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Checkbox } from "./ui/checkbox";
import Button from './Button';
import { useContactForm } from '../hooks/useContactForm';
import { useToast } from './ui/use-toast';

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    privacyAccepted: false
  });

  const { isSubmitting, submitStatus, submitForm, resetStatus } = useContactForm();
  const { toast } = useToast();

  // Handle form submission status changes
  useEffect(() => {
    if (submitStatus === 'success') {
      toast({
        title: "Nachricht gesendet!",
        description: "Vielen Dank für Ihre Anfrage. Wir melden uns schnellstmöglich bei Ihnen.",
        duration: 5000,
      });
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        privacyAccepted: false
      });
      resetStatus();
    } else if (submitStatus === 'error') {
      toast({
        title: "Fehler beim Senden",
        description: "Entschuldigung, es gab ein Problem beim Senden Ihrer Nachricht. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt.",
        variant: "destructive",
        duration: 5000,
      });
      resetStatus();
    }
  }, [submitStatus, toast, resetStatus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.privacyAccepted) {
      toast({
        title: "Datenschutz erforderlich",
        description: "Bitte stimmen Sie der Datenschutzerklärung zu, um fortzufahren.",
        variant: "destructive",
      });
      return;
    }

    await submitForm(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    });
  };

  const handlePrivacyChange = (checked: boolean) => {
    setFormData({
      ...formData,
      privacyAccepted: checked
    });
  };

  return (
    <div className="fade-in-up-delay">
      <div className="mb-8">
        <h3 className="text-3xl md:text-4xl font-bold text-primary-brand">
          Schreiben Sie uns
        </h3>
      </div>
      
      <div className="bg-white rounded-2xl p-6 shadow-elegant hover:shadow-elegant-lg transition-all duration-300 border border-gray-100">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-brand focus:border-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Ihr Name"
              />
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                E-Mail *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-brand focus:border-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="ihre.email@beispiel.de"
              />
            </div>
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
              Telefon *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-brand focus:border-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="Ihre Telefonnummer"
            />
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
              Nachricht *
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={6}
              disabled={isSubmitting}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-brand focus:border-transparent transition-colors resize-none disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="Beschreiben Sie Ihr Projekt oder Ihre Anfrage..."
            ></textarea>
          </div>

          {/* Privacy Policy Checkbox */}
          <div className="flex items-start space-x-3">
            <Checkbox
              id="privacy"
              checked={formData.privacyAccepted}
              onCheckedChange={handlePrivacyChange}
              required
              disabled={isSubmitting}
              className="mt-1 border-gray-300 data-[state=checked]:bg-primary-brand data-[state=checked]:border-primary-brand"
            />
            <label htmlFor="privacy" className="text-sm text-gray-600 leading-relaxed">
              Ich stimme der Verarbeitung meiner Daten gemäß der{' '}
              <Link 
                to="/datenschutz" 
                className="text-primary-brand hover:text-secondary-brand transition-colors underline"
              >
                Datenschutzerklärung
              </Link>
              {' '}zu. *
            </label>
          </div>

          <div className="pt-4">
            <Button 
              type="submit" 
              variant="primary" 
              size="lg" 
              className="w-full" 
              icon={isSubmitting ? undefined : <Send className="w-5 h-5" />}
              disabled={!formData.privacyAccepted || isSubmitting}
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Wird gesendet...
                </div>
              ) : (
                'Nachricht senden'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;
